import {
	OrderQuoteItemQuantityChangeKind,
	OrderQuoteItemQuantityChangeSeverity,
	type PreviewOrderQuoteItemQuantityChangeQuery,
} from '@codezo/graphql';
import { AlertTriangle, ArrowRight, Eye, Minus, MoveVertical, Plus, Users } from 'lucide-react';
import { cn } from '../../../../lib/utils';

type Preview = PreviewOrderQuoteItemQuantityChangeQuery['previewOrderQuoteItemQuantityChange'];
type Change = Preview['changes'][number];

/**
 * An existing tier whose value the operator changed. Each one is a choice —
 * carry the tier over and keep its priced work, or start the quantity fresh —
 * which is why they get a card of their own rather than a line in a list.
 */
export interface EditedTier {
	quantityId: string;
	fromQuantity: number;
	toQuantity: number;
	startOver: boolean;
}

interface QuantityChangeImpactProps {
	preview: Preview | null;
	editedTiers: EditedTier[];
	onToggleStartOver: (quantityId: string) => void;
	/** The preview on screen is for a payload we have already moved on from. */
	stale?: boolean;
}

const KIND_ICON: Record<OrderQuoteItemQuantityChangeKind, typeof Plus> = {
	[OrderQuoteItemQuantityChangeKind.Unchanged]: MoveVertical,
	[OrderQuoteItemQuantityChangeKind.Reordered]: MoveVertical,
	[OrderQuoteItemQuantityChangeKind.Repriced]: ArrowRight,
	[OrderQuoteItemQuantityChangeKind.Added]: Plus,
	[OrderQuoteItemQuantityChangeKind.Removed]: Minus,
};

const SEVERITY_TONE: Record<OrderQuoteItemQuantityChangeSeverity, string> = {
	[OrderQuoteItemQuantityChangeSeverity.None]: 'border-line-1 bg-surface-sunken',
	[OrderQuoteItemQuantityChangeSeverity.Review]: 'border-status-warn/25 bg-status-warn/5',
	[OrderQuoteItemQuantityChangeSeverity.Destructive]:
		'border-status-danger/25 bg-status-danger/5',
};

const SEVERITY_CHIP: Record<OrderQuoteItemQuantityChangeSeverity, string | null> = {
	[OrderQuoteItemQuantityChangeSeverity.None]: null,
	[OrderQuoteItemQuantityChangeSeverity.Review]: 'Needs review',
	[OrderQuoteItemQuantityChangeSeverity.Destructive]: 'Deletes priced work',
};

const SEVERITY_CHIP_TONE: Record<OrderQuoteItemQuantityChangeSeverity, string> = {
	[OrderQuoteItemQuantityChangeSeverity.None]: 'bg-surface-fill text-text-3',
	[OrderQuoteItemQuantityChangeSeverity.Review]: 'bg-status-warn/10 text-status-warn',
	[OrderQuoteItemQuantityChangeSeverity.Destructive]: 'bg-status-danger/10 text-status-danger',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The counts behind a change, as a readable list. */
function impactFacts(impact: Change['impact']): string[] {
	const facts: string[] = [];
	if (impact.pricedOfferCount > 0) facts.push(plural(impact.pricedOfferCount, 'price', 'prices'));
	if (impact.deliveryOptionCount > 0)
		facts.push(plural(impact.deliveryOptionCount, 'delivery option', 'delivery options'));
	if (impact.offers.length > 0) facts.push(plural(impact.offers.length, 'offer', 'offers'));
	return facts;
}

/** The loudest of a set of severities. */
function worstSeverity(changes: Change[]): OrderQuoteItemQuantityChangeSeverity {
	if (changes.some((c) => c.severity === OrderQuoteItemQuantityChangeSeverity.Destructive))
		return OrderQuoteItemQuantityChangeSeverity.Destructive;
	if (changes.some((c) => c.severity === OrderQuoteItemQuantityChangeSeverity.Review))
		return OrderQuoteItemQuantityChangeSeverity.Review;
	return OrderQuoteItemQuantityChangeSeverity.None;
}

function Quantity({ value, muted }: { value: number; muted?: boolean }) {
	return (
		<span
			className={cn(
				'font-mono text-base tabular-nums',
				muted ? 'text-text-4 line-through' : 'font-medium text-text-1'
			)}
		>
			{value.toLocaleString()}
		</span>
	);
}

/** One consequence: what the backend says, and the counts behind it. */
function ChangeDetail({ change, showIcon = true }: { change: Change; showIcon?: boolean }) {
	const Icon = KIND_ICON[change.kind];
	const facts = impactFacts(change.impact);
	const { impact } = change;
	const shownOffers = impact.offers.slice(0, 3);

	return (
		<div className="flex items-start gap-1.5">
			{showIcon && <Icon size={14} className="mt-1 shrink-0 text-text-4" />}
			<div className="flex min-w-0 flex-col gap-1">
				<span className="text-xs leading-relaxed text-text-2">{change.message}</span>

				{facts.length > 0 && (
					<span className="text-2xs text-text-4">{facts.join(' · ')}</span>
				)}

				{/* A committed combination means a client is holding a quote built on
				    this tier — the one count worth stopping for. */}
				{impact.masterSelectionCount > 0 && (
					<span className="flex items-center gap-1 text-2xs font-medium text-status-danger">
						<Users size={11} className="shrink-0" />
						{impact.masterSelectionCount === 1
							? '1 client has committed to a combination using this tier'
							: `${impact.masterSelectionCount} clients have committed to a combination using this tier`}
					</span>
				)}

				{impact.masterSelectionCount === 0 && impact.clientVisibleOfferCount > 0 && (
					<span className="flex items-center gap-1 text-2xs text-text-3">
						<Eye size={11} className="shrink-0" />
						{impact.clientVisibleOfferCount === 1
							? 'The client can already see this price'
							: `The client can already see this price in ${impact.clientVisibleOfferCount} offers`}
					</span>
				)}

				{shownOffers.length > 0 && (
					<div className="mt-0.5 flex flex-col gap-0.5">
						{shownOffers.map((offer) => (
							<span
								key={offer.orderQuoteOfferId}
								className="truncate text-2xs text-text-4"
							>
								{offer.offerName}
								<span className="text-text-4/60"> · {offer.batchName}</span>
							</span>
						))}
						{impact.offers.length > shownOffers.length && (
							<span className="text-2xs text-text-4">
								+{impact.offers.length - shownOffers.length} more
							</span>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

/** The keep-or-restart choice, as two options rather than one default and a link. */
function TierChoice({
	startOver,
	toQuantity,
	onSelect,
}: {
	startOver: boolean;
	toQuantity: number;
	onSelect: (startOver: boolean) => void;
}) {
	const options: Array<{ value: boolean; label: string; hint: string }> = [
		{ value: false, label: 'Keep the prices', hint: 'Carries the priced work over' },
		{
			value: true,
			label: 'Start over',
			hint: `${toQuantity.toLocaleString()} starts empty`,
		},
	];

	return (
		<div className="flex gap-1.5">
			{options.map((option) => {
				const active = option.value === startOver;
				return (
					<button
						key={String(option.value)}
						type="button"
						onClick={() => onSelect(option.value)}
						className={cn(
							'flex flex-1 cursor-pointer flex-col gap-0.5 rounded-lg border px-2 py-1.5 text-left transition-colors',
							active
								? 'border-text-1/20 bg-surface'
								: 'border-line-1 bg-transparent hover:border-line-2 hover:bg-surface/50'
						)}
					>
						<span
							className={cn(
								'text-xs font-medium',
								active ? 'text-text-1' : 'text-text-3'
							)}
						>
							{option.label}
						</span>
						<span className="text-2xs text-text-4">{option.hint}</span>
					</button>
				);
			})}
		</div>
	);
}

export function QuantityChangeImpact({
	preview,
	editedTiers,
	onToggleStartOver,
	stale,
}: QuantityChangeImpactProps) {
	if (!preview) return null;

	// Each edited tier owns its preview entries: one REPRICED while it is being
	// carried over, or a REMOVED plus an ADDED once it is being started fresh.
	// Everything left over is a plain add, removal or reorder.
	const claimed = new Set<Change>();
	const cards = editedTiers
		.map((tier) => {
			const changes = preview.changes.filter(
				(c) => c.orderQuoteItemQuantityId === tier.quantityId
			);
			changes.forEach((c) => claimed.add(c));

			// The tier this one is reborn as. Claimed so it does not surface as a
			// card of its own, but not rendered — "starts empty" is already the
			// Start over option's own description, and repeating it here made the
			// card jump every time the operator switched between the two.
			if (tier.startOver) {
				const added = preview.changes.find(
					(c) =>
						c.kind === OrderQuoteItemQuantityChangeKind.Added &&
						c.newQuantity === tier.toQuantity
				);
				if (added) claimed.add(added);
			}

			return { tier, changes };
		})
		.filter((card) => card.changes.length > 0);

	const loose = preview.changes.filter(
		(c) => !claimed.has(c) && c.kind !== OrderQuoteItemQuantityChangeKind.Unchanged
	);

	return (
		<div className="flex flex-col gap-2">
			{preview.errors.length > 0 && (
				<div className="flex flex-col gap-1 rounded-lg border border-status-danger/25 bg-status-danger/5 px-2.5 py-2">
					{preview.errors.map((error) => (
						<div
							key={error.key}
							className="flex items-start gap-1.5 text-xs text-status-danger"
						>
							<AlertTriangle size={14} className="mt-1 shrink-0" />
							<span className="leading-relaxed">{error.message}</span>
						</div>
					))}
				</div>
			)}

			{cards.map(({ tier, changes }) => {
				const severity = worstSeverity(changes);
				const chip = SEVERITY_CHIP[severity];

				return (
					<div
						key={tier.quantityId}
						className={cn('rounded-lg border px-2.5 py-2', SEVERITY_TONE[severity])}
					>
						<div className="flex items-center justify-between gap-2">
							<div className="flex items-center gap-1.5">
								<Quantity value={tier.fromQuantity} muted={tier.startOver} />
								<ArrowRight size={12} className="shrink-0 text-text-4" />
								<Quantity value={tier.toQuantity} />
							</div>
							{chip && (
								<span
									className={cn(
										'shrink-0 rounded-md px-1.5 py-0.5 text-2xs font-medium',
										SEVERITY_CHIP_TONE[severity]
									)}
								>
									{chip}
								</span>
							)}
						</div>

						<div className="mt-2">
							<TierChoice
								startOver={tier.startOver}
								toQuantity={tier.toQuantity}
								onSelect={(next) => {
									if (next !== tier.startOver) onToggleStartOver(tier.quantityId);
								}}
							/>
						</div>

						<div
							className={cn(
								'mt-2 flex flex-col gap-2 border-t border-line-1/60 pt-2 transition-opacity duration-150',
								stale && 'opacity-40'
							)}
						>
							{changes.map((change) => (
								<ChangeDetail
									key={`${change.kind}:${change.orderQuoteItemQuantityId ?? change.newQuantity}`}
									change={change}
								/>
							))}
						</div>
					</div>
				);
			})}

			{loose.map((change) => (
				<div
					key={`${change.kind}:${change.orderQuoteItemQuantityId ?? change.newQuantity}`}
					className={cn('rounded-lg border px-2.5 py-2', SEVERITY_TONE[change.severity])}
				>
					<div className="mb-1.5 flex items-center justify-between gap-2">
						<div className="flex items-center gap-1.5">
							{change.kind === OrderQuoteItemQuantityChangeKind.Removed ? (
								<Minus size={12} className="shrink-0 text-text-4" />
							) : change.kind === OrderQuoteItemQuantityChangeKind.Added ? (
								<Plus size={12} className="shrink-0 text-text-4" />
							) : (
								<MoveVertical size={12} className="shrink-0 text-text-4" />
							)}
							<Quantity
								value={change.newQuantity ?? change.currentQuantity ?? 0}
								muted={change.kind === OrderQuoteItemQuantityChangeKind.Removed}
							/>
						</div>
						{SEVERITY_CHIP[change.severity] && (
							<span
								className={cn(
									'shrink-0 rounded-md px-1.5 py-0.5 text-2xs font-medium',
									SEVERITY_CHIP_TONE[change.severity]
								)}
							>
								{SEVERITY_CHIP[change.severity]}
							</span>
						)}
					</div>
					<ChangeDetail change={change} showIcon={false} />
				</div>
			))}
		</div>
	);
}
